import React, { useState, useEffect, useCallback } from 'react';
import cn from 'classnames';
import { confirmAlert } from 'react-confirm-alert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTrash, faPencil } from '@fortawesome/pro-regular-svg-icons';
import { getConciergeRequestCodes, createConciergeRequestCode, updateConciergeRequestCode, deleteConciergeRequestCode } from '../../api/noir';
import Tooltip from '../General/Tooltip';
import moment from 'moment';
import CodeFormModal from './CodeFormModal';
import CodeRedemptionsModal from './CodeRedemptionsModal';
import './NoirRedeemableCodes.scss';

const COLUMNS = [
  { key: 'code', label: 'Code' },
  { key: 'requestsGrantedPerRedemption', label: 'Requests Per Use' },
  { key: 'uses', label: 'Redemptions' },
  { key: 'perUserLimit', label: 'Per-User Limit' },
  { key: 'totalLimit', label: 'Total Limit' },
  { key: 'users', label: 'Users' },
  { key: 'enableQuickSetup', label: 'Quick Setup' },
  { key: 'expiresAt', label: 'Expires' },
  { key: 'createdAt', label: 'Created' },
  { key: 'actions', label: '' }
];

const NoirRedeemableCodes = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCodes = useCallback(() => {
    setLoading(true);
    getConciergeRequestCodes()
      .then(resp => setCodes(resp || []))
      .catch(() => window.ALERT.error('Failed to load redeemable codes.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const openCreateModal = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <CodeFormModal
          header='Create Redeemable Code'
          onCancel={onClose}
          onSubmit={data => {
            onClose();
            createConciergeRequestCode(data)
              .then(() => {
                window.ALERT.success(`Created code "${data.code}".`);
                fetchCodes();
              })
              .catch(err => window.ALERT.error(err || 'Failed to create code.'));
          }}
        />
      )
    });
  };

  const openEditModal = codeRow => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <CodeFormModal
          header={`Edit "${codeRow.code}"`}
          initial={codeRow}
          isEditing
          onCancel={onClose}
          onSubmit={updates => {
            onClose();
            updateConciergeRequestCode(updates)
              .then(() => {
                window.ALERT.success(`Updated code "${updates.code}".`);
                fetchCodes();
              })
              .catch(err => window.ALERT.error(err || 'Failed to update code.'));
          }}
        />
      )
    });
  };

  const openRedemptionsModal = codeRow => {
    confirmAlert({
      customUI: ({ onClose }) => <CodeRedemptionsModal code={codeRow.code} onClose={onClose} />
    });
  };

  const openDeleteModal = codeRow => {
    confirmAlert({
      title: `Delete "${codeRow.code}"?`,
      message: `This will remove the code for ${codeRow.isGated ? `${codeRow.allowedUserCount} gated user(s)` : 'all users'}. This action cannot be undone.`,
      buttons: [
        { label: 'Cancel', className: 'cancel', onClick: () => {} },
        {
          label: 'Delete',
          onClick: () => {
            deleteConciergeRequestCode(codeRow.code)
              .then(() => {
                window.ALERT.success(`Deleted code "${codeRow.code}".`);
                setCodes(prev => prev.filter(c => c.code !== codeRow.code));
              })
              .catch(err => window.ALERT.error(err || 'Failed to delete code.'));
          }
        }
      ]
    });
  };

  return (
    <div className='noir-redeemable-codes'>
      <div className='codes-toolbar'>
        <div className='codes-count'>
          {codes.length} code{codes.length !== 1 ? 's' : ''}
        </div>
        <button className='button-add' onClick={openCreateModal}>
          Add Code
        </button>
      </div>

      {loading ? (
        <div className='codes-placeholder'>
          <FontAwesomeIcon icon={faSpinner} spin />
          Loading codes...
        </div>
      ) : !codes.length ? (
        <div className='codes-placeholder'>No redeemable codes yet.</div>
      ) : (
        <div className='codes-table'>
          <div className='codes-grid header'>
            {COLUMNS.map(col => (
              <div key={col.key} className='th'>
                {col.label}
              </div>
            ))}
          </div>
          {codes.map(codeRow => {
            const isExpired = codeRow.expiresAt && moment(codeRow.expiresAt).isBefore(moment());
            const isFull = codeRow.totalLimit != null && codeRow.totalUses >= codeRow.totalLimit;
            return (
              <div key={codeRow.code} className={cn('codes-grid row', { expired: isExpired })}>
                <div className='td code-value'>{codeRow.code}</div>
                <div className='td'>{codeRow.requestsGrantedPerRedemption}</div>
                <div className='td'>
                  {codeRow.totalUses > 0 ? (
                    <Tooltip
                      message={`Redeemed by ${Number(codeRow.redeemedUserCount || 0).toLocaleString()} user${codeRow.redeemedUserCount !== 1 ? 's' : ''} · click to view`}
                      position='top'
                    >
                      <button type='button' className='usage-button' onClick={() => openRedemptionsModal(codeRow)}>
                        <span className={cn('usage', { full: isFull })}>{codeRow.totalUses}</span>
                      </button>
                    </Tooltip>
                  ) : (
                    <span className={cn('usage', { full: isFull })}>{codeRow.totalUses}</span>
                  )}
                </div>
                <div className='td'>{codeRow.perUserLimit != null ? codeRow.perUserLimit.toLocaleString() : '-'}</div>
                <div className='td'>{codeRow.totalLimit != null ? codeRow.totalLimit.toLocaleString() : '-'}</div>
                <div className='td'>{codeRow.isGated ? `${codeRow.allowedUserCount.toLocaleString()} user${codeRow.allowedUserCount !== 1 ? 's' : ''}` : 'All'}</div>
                <div className='td'>{codeRow.enableQuickSetup ? 'Yes' : '-'}</div>
                <div className='td'>
                  {codeRow.expiresAt ? <span className={cn('expires', { expired: isExpired })}>{moment(codeRow.expiresAt).format('MMM D, YYYY')}</span> : '-'}
                </div>
                <div className='td'>{codeRow.createdAt ? moment(codeRow.createdAt).format('MMM D, YYYY') : '-'}</div>
                <div className='td actions'>
                  <div className='action-icon' onClick={() => openEditModal(codeRow)}>
                    <FontAwesomeIcon icon={faPencil} />
                  </div>
                  <div className='action-icon delete' onClick={() => openDeleteModal(codeRow)}>
                    <FontAwesomeIcon icon={faTrash} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NoirRedeemableCodes;
